import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useI18n } from "../context/I18nContext";
import { Placeholder } from "./Placeholder";

const CartDrawer = () => {
  const { open, setOpen, items, updateQuantity, removeItem, subtotal } =
    useCart();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#FFFBF5] border-l border-[#F39C12]/20 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-[#2C3E50] text-left">
            {t("cart")}
          </SheetTitle>
          <SheetDescription className="text-left text-[#7F8C8D]">
            {t("cod_label")} · {t("free_shipping")}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6">
          {items.length === 0 ? (
            <div className="text-center py-16 flex flex-col items-center">
              <ShoppingBag className="w-12 h-12 text-[#F39C12]/60 mb-4" />
              <p className="font-display text-lg text-[#2C3E50]">
                Your cart is empty
              </p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li
                  key={item.product_id}
                  className="flex gap-3 bg-white border border-[#F39C12]/15 rounded-2xl p-3"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <div className="w-20 h-20 shrink-0 rounded-xl bg-[#FFF5E1] border-2 border-dashed border-[#F39C12]/40 flex items-center justify-center">
                    <span className="text-[10px] text-[#F39C12] font-semibold">
                      photo
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-semibold text-sm text-[#2C3E50] truncate">
                      {item.name}
                    </p>
                    <p className="text-xs text-[#7F8C8D]">{t("cod_label")}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center border border-[#F39C12]/30 rounded-full overflow-hidden">
                        <button
                          data-testid={`qty-dec-${item.product_id}`}
                          onClick={() =>
                            updateQuantity(item.product_id, item.quantity - 1)
                          }
                          className="px-2 py-1 hover:bg-[#FFF5E1]"
                          aria-label="Decrease"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-3 text-sm font-medium">
                          {item.quantity}
                        </span>
                        <button
                          data-testid={`qty-inc-${item.product_id}`}
                          onClick={() =>
                            updateQuantity(item.product_id, item.quantity + 1)
                          }
                          className="px-2 py-1 hover:bg-[#FFF5E1]"
                          aria-label="Increase"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="font-display font-bold text-[#2C3E50] text-sm">
                          ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                        </p>
                        <button
                          data-testid={`remove-item-${item.product_id}`}
                          onClick={() => removeItem(item.product_id)}
                          className="text-xs text-[#D63031] inline-flex items-center gap-1 hover:underline"
                        >
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#F39C12]/20 pt-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#7F8C8D]">{t("subtotal")}</span>
              <span className="font-display font-bold text-[#2C3E50]">
                ₹{subtotal.toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-[#27AE60] font-medium">
              <span>{t("shipping")}</span>
              <span>{t("free")}</span>
            </div>
            <Button
              data-testid="checkout-from-cart-btn"
              onClick={handleCheckout}
              className="w-full cta-red rounded-full h-12 font-semibold"
            >
              {t("place_order")}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
